import { ZButton } from "@/shared/components/button";
import { ZTableColumnProps } from "@/shared/components/table";
import { Avatar, AvatarGroup, Tooltip } from "@nextui-org/react";
import { useCallback, useState } from "react";
import dayjs from "dayjs";
import { BreachHistoryItem } from "../types/breachHistory";
import VendorLogo from "@/shared/components/VendorSearchAutocomplete/VendorLogo";

/**
 * Helper component to render vendor name and product with icon/logo
 */
function VendorCell({ breach }: { breach: BreachHistoryItem }) {
  const { vendor_name, product_name, vendor_logo_url, vendor_icon } = breach;
  
  // Combine vendor and product name with SPACE separator
  const vendorProductName = [vendor_name, product_name]
    .filter(name => name && name.trim())
    .join(' ') || '-';

  // First letter of vendor name for fallback avatar
  const firstLetter = vendor_name?.trim().charAt(0).toUpperCase() || '?';

  return (
    <div className="flex items-center gap-2 min-h-[40px] text-[#333]">
      {vendor_logo_url ? (
        <VendorLogo
          src={vendor_logo_url}
          alt={vendor_name}
          needsAuthToken={true}
          size={24}
          radius="sm"
        />
      ) : (
        <div
          className="flex items-center justify-center"
          style={{
            width: '24px',
            height: '24px',
            background: '#FAFAFA',
            border: '1px solid #E4E4E7',
            borderRadius: '24px',
            padding: '2px',
          }}
        >
          <span
            style={{
              fontFamily: 'Inter',
              fontWeight: 600,
              fontSize: '14px',
              lineHeight: '20px',
              textAlign: 'center',
              color: '#71717A',
            }}
          >
            {firstLetter}
          </span>
        </div>
      )}
      <span className="text-sm font-medium">{vendorProductName}</span>
    </div>
  );
}

/**
 * Helper component to render references with tooltips
 */
function ReferencesCell({ breach }: { breach: BreachHistoryItem }) {
  const { references, id } = breach;
  const [tooltipStates, setTooltipStates] = useState<{ [key: string]: boolean }>({});

  const handleOpenChange = useCallback((key: string, open: boolean) => {
    setTooltipStates((prev) => ({ ...prev, [key]: open }));
  }, []);

  const handleViewClick = useCallback(
    (url: string, key: string) => {
      handleOpenChange(key, false);
      window.open(url, "_blank");
    },
    [handleOpenChange]
  );

  if (!references || references.length === 0) {
    return <span className="text-sm text-gray-400">-</span>;
  }

  return (
    <div className="flex items-center gap-2">
      <AvatarGroup
        isBordered
        classNames={{
          count:
            "flex relative justify-center items-center box-border overflow-hidden align-middle z-0 outline-none data-[focus-visible=true]:z-10 data-[focus-visible=true]:outline-2 data-[focus-visible=true]:outline-focus data-[focus-visible=true]:outline-offset-2 text-primary-foreground rounded-full ring-0 bg-[#006FEE] bg-opacity-20 text-primary ring-offset-2 ring-offset-background dark:ring-offset-background-dark -ms-0 data-[hover=true]:-translate-x-1 rtl:data-[hover=true]:translate-x-1 transition-transform data-[focus-visible=true]:-translate-x-1 rtl:data-[focus-visible=true]:translate-x-1 ring-primary w-4 h-4 text-tiny",
        }}
        color="primary"
      >
        {references.map((url, index) => {
          const tooltipKey = `${id}_${index}`;
          // Extract domain from URL for display
          let domain = '';
          try {
            const urlObj = new URL(url);
            domain = urlObj.hostname;
          } catch {
            domain = url;
          }

          return (
            <Tooltip
              showArrow={true}
              placement="top"
              shouldFlip={true}
              key={tooltipKey}
              isDismissable={true}
              closeDelay={100}
              isOpen={tooltipStates[tooltipKey]}
              onOpenChange={(open) => handleOpenChange(tooltipKey, open)}
              classNames={{
                content: "bg-black/95 backdrop-blur-md text-white text-tiny p-3",
                arrow: "backdrop-blur-md",
                base: "before:bg-black/95",
              }}
              content={
                <div className="flex flex-col gap-y-[0.5rem] max-w-96">
                  <div className="max-h-[5rem] overflow-hidden">
                    <p className="line-clamp-4 text-white break-all">{url}</p>
                  </div>
                  <div className="flex gap-3">
                    <p className="text-tiny text-[#A5A5A5] break-all">{domain}</p>
                  </div>
                  <div className="self-end flex gap-2">
                    <ZButton size="sm" type="button" onClick={() => handleViewClick(url, tooltipKey)}>
                      View Reference
                    </ZButton>
                  </div>
                </div>
              }
            >
              <Avatar
                key={tooltipKey}
                classNames={{
                  icon: "[&>span]:text-primary",
                  base: `
                      flex relative justify-center items-center box-border overflow-hidden align-middle z-0 outline-none
                      data-[focus-visible=true]:z-10 data-[focus-visible=true]:outline-2 data-[focus-visible=true]:outline-focus
                      data-[focus-visible=true]:outline-offset-2 text-primary-foreground rounded-full ring-0 bg-[#006FEE]
                      bg-opacity-20 text-primary ring-offset-2 ring-offset-background dark:ring-offset-background-dark -ms-0
                      data-[hover=true]:-translate-x-1 rtl:data-[hover=true]:translate-x-1 transition-transform
                      data-[focus-visible=true]:-translate-x-1 rtl:data-[focus-visible=true]:translate-x-1 ring-primary
                      w-4 h-4 text-[10px]
                    `,
                }}
                icon={<span>{index + 1}</span>}
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenChange(tooltipKey, !tooltipStates[tooltipKey]);
                }}
              >
                {index + 1}
              </Avatar>
            </Tooltip>
          );
        })}
      </AvatarGroup>
    </div>
  );
}

export const BreachHistoryColumns: ZTableColumnProps<BreachHistoryItem>[] = [
  // 1. Third-Party & Product (with proper icons)
  {
    type: "custom",
    fieldName: "third_party_product",
    headerName: "Third-Party & Product",
    sortable: true,
    getSortValue: ({ vendor_name }) => vendor_name,
    renderCell: (breach) => <VendorCell breach={breach} />,
  },
  
  // 2. Breach Date
  {
    type: "custom",
    fieldName: "breach_date",
    headerName: "Breach Date",
    sortable: true,
    getSortValue: ({ breach_date }) => breach_date,
    renderCell: ({ breach_date }) => {
      if (!breach_date) return <span className="text-gray-400">-</span>;
      
      // Format: handle both "2018" and "2024-04-17"
      const formattedDate = breach_date.length === 4
        ? breach_date
        : dayjs(breach_date).isValid()
        ? dayjs(breach_date).format('MMM D, YYYY')
        : breach_date;
      
      return <span className="text-sm text-gray-700">{formattedDate}</span>;
    },
  },
  
  // 3. Event (only breach_text, no title)
  {
    type: "custom",
    fieldName: "breach_text",
    headerName: "Event",
    renderCell: ({ breach_text }) => (
      <div className="py-2 max-w-xl">
        <p className="text-sm text-gray-600 leading-relaxed">
          {breach_text || '-'}
        </p>
      </div>
    ),
  },
  
  // 4. References
  {
    type: "custom",
    fieldName: "references",
    headerName: "References",
    renderCell: (breach) => <ReferencesCell breach={breach} />,
  },
];
